import { useDroppable } from '@dnd-kit/core';
import { Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { CrosshairIcon } from '@vizit/icons';
import { colors, cx } from '@vizit/theme';
import type React from 'react';

interface DroppableAreaProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Unique identifier for the droppable area
   *
   * @required
   * @type {string}
   */
  id: string;

  /**
   * Whether the droppable area is disabled
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  disabled?: boolean;

  /**
   * Additional data to pass to the drop event
   *
   * @optional
   * @type {Record<string, unknown>}
   */
  data?: Record<string, unknown>;

  /**
   * Whether an item is currently being dragged (to show initial feedback)
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  isDragActive?: boolean;

  /**
   * Content to display inside the droppable area
   *
   * @optional
   * @type {React.ReactNode}
   */
  children?: React.ReactNode;

  /**
   * Supporting text to show below the placeholder
   *
   * @optional
   * @type {string}
   * @default "Drag and drop asset here from sidebar"
   */
  supportingText?: string;
}

const baseStyles = cx(
  'relative flex flex-col items-center justify-center',
  'transition-all duration-200 ease-in-out',
  'aspect-square',
  'px-5 py-4',
  'cursor-default',
);

/**
 * DroppableArea component provides a visual drop target with feedback states
 *
 * Features:
 * - Default state: Dashed border with primary color, white background
 * - Hover/drag active state: Solid brand border with light gray background
 * - Flexible sizing based on content and parent container
 * - Horizontal layout with icon and supporting text
 *
 * @example
 * ```tsx
 * <DroppableArea
 *   id="carousel-dropzone"
 *   isDragActive={isDragging}
 *   supportingText="Drag and drop asset here from sidebar"
 * >
 *   {children}
 * </DroppableArea>
 * ```
 */
function DroppableArea(props: DroppableAreaProps) {
  const {
    id,
    disabled = false,
    data,
    isDragActive = false,
    children,
    supportingText = 'Drag and drop asset here from sidebar',
    className,
    onMouseEnter,
    onMouseLeave,
    ...other
  } = props;

  const { isOver, setNodeRef } = useDroppable({
    id,
    disabled,
    data,
  });

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    onMouseEnter?.(event);
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    onMouseLeave?.(event);
  };

  const getStateStyles = () => {
    if (isOver && isDragActive) {
      return cx('border border-solid border-brand-500', 'bg-primary-hover');
    }

    if (isDragActive) {
      return cx('border border-solid border-brand-500', 'bg-primary');
    }

    return cx('border border-solid border-primary', 'bg-primary');
  };

  return (
    <div
      ref={setNodeRef}
      className={cx(baseStyles, getStateStyles(), className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...other}
    >
      {children || (
        <div className="flex gap-3 items-center justify-center text-center w-full">
          <div className="w-20 h-20 rounded-full flex items-center justify-center bg-tertiary">
            <CrosshairIcon color={colors['gray-utility'][500]} />
          </div>
          <div className="flex items-center justify-center">
            <Text showEllipsis={false} size={TextSize.Xs} weight={TextWeight.Medium} color={TextColor.Tertiary}>
              {supportingText}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}

export { DroppableArea };
export type { DroppableAreaProps };
